import React from 'react';
import { Document, Page, View, Text, Image, StyleSheet, PDFViewer, PDFDownloadLink } from '@react-pdf/renderer';
import { Box, Button } from '@mui/material';
import { pdfTheme } from '../../../utils/pdfTheme';
import { studentName } from './hubUtils';

const c = pdfTheme.colors;

const s = StyleSheet.create({
  page:         { padding: '8mm 7mm', fontSize: 6.5, fontFamily: 'Helvetica', backgroundColor: '#fff' },

  // ── Header ──────────────────────────────────────────────────────────────────
  header:       { flexDirection: 'row', alignItems: 'center', marginBottom: 5, paddingBottom: 5, borderBottom: `2pt solid ${c.primary}`, gap: 8 },
  logoBox:      { width: 55, height: 55, alignItems: 'center', justifyContent: 'center', borderRadius: 2 },
  logoText:     { fontSize: 5, color: c.primary, textAlign: 'center' },
  headerCenter: { flex: 1, alignItems: 'center' },
  schoolName:   { fontSize: 12, fontFamily: 'Helvetica-Bold', color: c.primary, textTransform: 'uppercase', textAlign: 'center', letterSpacing: 0.5 },
  motto:        { fontSize: 6.5, color: '#555', fontStyle: 'italic', textAlign: 'center', marginTop: 1 },
  address:      { fontSize: 6, color: '#666', textAlign: 'center', marginTop: 1 },
  docTitle:     { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#fff', backgroundColor: c.primary, textAlign: 'center', marginTop: 4, textTransform: 'uppercase', paddingVertical: 2, paddingHorizontal: 10, borderRadius: 2 },

  // ── Meta strip ──────────────────────────────────────────────────────────────
  metaStrip:    { flexDirection: 'row', borderRadius: 2, marginBottom: 6, border: `1pt solid ${c.border}`, overflow: 'hidden' },
  metaItem:     { flex: 1, alignItems: 'center', padding: '5 6', borderRight: `1pt solid ${c.border}` },
  metaItemLast: { borderRight: 'none' },
  metaLabel:    { fontSize: 6, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 },
  metaValue:    { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#222' },

  // ── Table
  table:        { width: '100%', borderLeft: `0.5pt solid ${c.border}`, borderTop: `0.5pt solid ${c.border}` },

  // Header rows
  theadTop:     { flexDirection: 'row', backgroundColor: c.primary },
  theadSub:     { flexDirection: 'row', backgroundColor: '#388e3c' },
  th:           { fontFamily: 'Helvetica-Bold', color: '#fff', padding: '8 5', borderRight: `0.5pt solid rgba(255,255,255,0.25)`, textAlign: 'center', fontSize: 8 },
  thLeft:       { textAlign: 'left' },

  // Data rows
  row:          { flexDirection: 'row', borderBottom: `0.5pt solid ${c.border}` },
  rowAlt:       { backgroundColor: '#f9fbe7' },
  td:           { fontSize: 9, padding: '7 5', borderRight: `0.5pt solid ${c.border}`, textAlign: 'center' },
  tdLeft:       { textAlign: 'left' },
  tdBold:       { fontFamily: 'Helvetica-Bold' },
  tdGray:       { color: '#bbb', fontStyle: 'italic' },
  tdGreen:      { color: c.primary, fontFamily: 'Helvetica-Bold' },
  tdBlue:       { color: '#1565c0', fontFamily: 'Helvetica-Bold' },

  // Footer rows
  footerRow:    { flexDirection: 'row', borderBottom: `0.5pt solid ${c.border}`, backgroundColor: '#e8f5e9' },
  footerRowDiv: { flexDirection: 'row', borderBottom: `0.5pt solid ${c.border}`, backgroundColor: '#e3f2fd' },
  footerRowPos: { flexDirection: 'row', backgroundColor: '#fff9c4' },
  ftd:          { fontSize: 6, padding: '2 2', borderRight: `0.5pt solid ${c.border}`, textAlign: 'center', fontFamily: 'Helvetica-Bold' },
  ftdLeft:      { textAlign: 'left' },

  // Page footer
  pageFooter:   { borderTop: `1pt solid ${c.border}`, marginTop: 5, paddingTop: 3, fontSize: 5.5, color: '#aaa', textAlign: 'center' },
});

// ── Width calculator ──────────────────────────────────────────────────────────
// Fixed cols: # + Student + Reg = ~28%
// Summary cols: Total + Agg + Div + Pos = ~14%
// Remaining: split across subjects × assessmentCols
const calcWidths = (subjectCount, colCount, showAggDiv = true, showPosition = true) => {
  const fixed   = { no: 3, student: 18, reg: 9 };           // %
  const summary = showAggDiv
    ? (showPosition ? { total: 5, agg: 5, div: 8, pos: 8 } : { total: 5, agg: 5, div: 8 })
    : { total: 7 };                                          // %
  const fixedSum   = Object.values(fixed).reduce((a, b) => a + b, 0);
  const summarySum = Object.values(summary).reduce((a, b) => a + b, 0);
  const available  = 100 - fixedSum - summarySum;
  const cellW = subjectCount * colCount > 0
    ? Math.max(3.5, parseFloat((available / (subjectCount * colCount)).toFixed(2)))
    : 6;
  return { ...fixed, ...summary, cell: cellW };
};

const pct = (w) => `${w}%`;

const rawVal = (a) => {
  if (!a) return null;
  if (a.is_absent || a.is_exempted) return null;
  const v = parseFloat(a.score ?? a.marks_obtained ?? a.raw_score ?? 0);
  return isNaN(v) ? null : v;
};

const markStr = (a) => {
  if (!a) return '-';
  if (a.is_absent) return 'ABS';
  if (a.is_exempted) return 'EXM';
  const v = rawVal(a);
  return v != null ? v.toFixed(0) : '-';
};

const divColor = (div) => {
  if (!div) return '#888';
  if (div.includes('1')) return '#1565c0';
  if (div.includes('2')) return '#2e7d32';
  if (div.includes('3')) return '#e65100';
  if (div.includes('4')) return '#c62828';
  return '#888';
};

// ── PDF Document ──────────────────────────────────────────────────────────────
const ClassMarkListPdfDoc = ({ filteredRows, subjects, assessmentCols = [], classLabel, termLabel, school, showAggDiv = true, showPosition = true }) => {
  const cols = assessmentCols.length > 0 ? assessmentCols : [{ code: 'Marks', max_marks: '-' }];
  const w    = calcWidths(subjects.length, cols.length, showAggDiv, showPosition);

  const schoolName    = school?.name    || 'School Name';
  const schoolMotto   = school?.motto   || '';
  const schoolAddress = [school?.address, school?.city, school?.district].filter(Boolean).join(', ');
  const schoolPhone   = school?.phone   || '';
  const schoolLogo    = school?.logo    || null;

  // Pre-compute column totals for footer
  const colTotals = {};
  subjects.forEach((subj) => {
    cols.forEach((col) => {
      const key = `${subj.id}-${col.code}`;
      colTotals[key] = filteredRows.reduce((sum, row) => {
        const v = rawVal((row.marksBySubject[subj.id] || {})[col.code]);
        return sum + (v ?? 0);
      }, 0);
    });
  });

  const grandTotal = filteredRows.reduce((s, r) => s + (r.total ?? 0), 0);

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={s.page}>

        {/* ── School Header ── */}
        <View style={s.header}>
          {schoolLogo && (
            <View style={s.logoBox}>
              <Image src={schoolLogo} style={{ width: 51, height: 51, objectFit: 'contain' }} />
            </View>
          )}
          <View style={s.headerCenter}>
            <Text style={s.schoolName}>{schoolName}</Text>
            {schoolMotto   ? <Text style={s.motto}>{schoolMotto}</Text>   : null}
            {schoolAddress ? <Text style={s.address}>{schoolAddress}</Text> : null}
            {schoolPhone   ? <Text style={s.address}>Tel: {schoolPhone}</Text> : null}
            <Text style={s.docTitle}>Class Mark List</Text>
          </View>
        </View>

        {/* ── Meta strip ── */}
        <View style={s.metaStrip}>
          {[
            ['Class',     classLabel.split(' · ')[0] || classLabel],
            ['Term',      [classLabel.split(' · ')[1], classLabel.split(' · ')[2]].filter(Boolean).join(' ') || termLabel],
            ['Students',  String(filteredRows.length)],
            ['Subjects',  String(subjects.length)],
            ['Assessments', assessmentCols.length > 0 ? assessmentCols.map((c) => c.code).join(', ') : cols.map((c) => c.code).join(', ')],
            ['Date',   new Date().toLocaleDateString()],
          ].map(([label, val], i, arr) => (
            <View key={label} style={[s.metaItem, i === arr.length - 1 ? s.metaItemLast : {}]}>
              <Text style={s.metaLabel}>{label}</Text>
              <Text style={s.metaValue}>{val}</Text>
            </View>
          ))}
        </View>

        {/* ── Table ── */}
        <View style={s.table}>

          {/* Header row 1 — subject group spans */}
          <View style={s.theadTop} fixed>
            <View style={[s.th, s.thLeft, { width: pct(w.no) }]}><Text>#</Text></View>
            <View style={[s.th, s.thLeft, { width: pct(w.student) }]}><Text>Student</Text></View>
            <View style={[s.th, { width: pct(w.reg) }]}><Text>Reg No</Text></View>
            {subjects.map((subj) => (
              <View key={subj.id} style={[s.th, { width: pct(w.cell * cols.length), borderLeft: '0.5pt solid rgba(255,255,255,0.4)' }]}>
                <Text>{subj.code || subj.name}</Text>
              </View>
            ))}
            <View style={[s.th, { width: pct(w.total) }]}><Text>Total</Text></View>
            {showAggDiv && <View style={[s.th, { width: pct(w.agg) }]}><Text>Agg</Text></View>}
            {showAggDiv && <View style={[s.th, { width: pct(w.div) }]}><Text>Div</Text></View>}
            {showAggDiv && showPosition && <View style={[s.th, { width: pct(w.pos) }]}><Text>Position</Text></View>}
          </View>

          {/* Header row 2 — assessment code sub-headers */}
          {cols.length > 1 && (
            <View style={s.theadSub} fixed>
              <View style={[s.th, { width: pct(w.no + w.student + w.reg) }]}><Text> </Text></View>
              {subjects.map((subj) =>
                cols.map((col) => (
                  <View key={`${subj.id}-${col.code}`} style={[s.th, { width: pct(w.cell), fontSize: 7.5 }]}>
                    <Text>{col.code}</Text>
                    <Text style={{ fontSize: 6.5, color: 'rgba(255,255,255,0.7)' }}>/{col.max_marks}</Text>
                  </View>
                ))
              )}
              <View style={[s.th, { width: pct(w.total + (w.agg ?? 0) + (w.div ?? 0) + (w.pos ?? 0)) }]}><Text> </Text></View>
            </View>
          )}

          {/* Data rows */}
          {filteredRows.map((row, idx) => (
            <View key={row.student.id} wrap={false} style={[s.row, idx % 2 !== 0 ? s.rowAlt : {}]}>
              <View style={[s.td, { width: pct(w.no), color: '#aaa' }]}><Text>{idx + 1}</Text></View>
              <View style={[s.td, s.tdLeft, s.tdBold, { width: pct(w.student) }]}>
                <Text>{studentName(row.student)}</Text>
              </View>
              <View style={[s.td, { width: pct(w.reg), fontSize: 8 }]}>
                <Text>{row.student?.registration_no || '-'}</Text>
              </View>

              {subjects.map((subj) =>
                cols.map((col) => {
                  const a   = (row.marksBySubject[subj.id] || {})[col.code];
                  const val = markStr(a);
                  const isSpecial = val === 'ABS' || val === 'EXM';
                  return (
                    <View key={`${subj.id}-${col.code}`} style={[s.td, { width: pct(w.cell) }]}>
                      <Text style={isSpecial ? s.tdGray : s.tdBold}>{val}</Text>
                    </View>
                  );
                })
              )}

              <View style={[s.td, s.tdBold, { width: pct(w.total), backgroundColor: '#fffde7' }]}>
                <Text>{(row.total ?? 0).toFixed(0)}</Text>
              </View>
              {showAggDiv && (
                <View style={[s.td, s.tdGreen, { width: pct(w.agg) }]}>
                  <Text>{row.aggregate ?? '-'}</Text>
                </View>
              )}
              {showAggDiv && (
                <View style={[s.td, { width: pct(w.div), fontFamily: 'Helvetica-Bold', color: divColor(row.division) }]}>
                  <Text>{row.division ?? '-'}</Text>
                </View>
              )}
              {showAggDiv && showPosition && (
                <View style={[s.td, s.tdBlue, { width: pct(w.pos) }]}>
                  <Text>
                    {row.class_position != null
                      ? `${row.class_position}${row.class_total_students ? `/${row.class_total_students}` : ''}`
                      : '-'}
                  </Text>
                </View>
              )}
            </View>
          ))}

          {/* ── Footer: Totals row ── */}
          {/* <View style={s.footerRow}>
            <View style={[s.ftd, s.ftdLeft, { width: pct(w.no + w.student + w.reg) }]}>
              <Text>TOTALS</Text>
            </View>
            {subjects.map((subj) =>
              cols.map((col) => (
                <View key={`tot-${subj.id}-${col.code}`} style={[s.ftd, { width: pct(w.cell) }]}>
                  <Text>{(colTotals[`${subj.id}-${col.code}`] ?? 0).toFixed(0)}</Text>
                </View>
              ))
            )}
            <View style={[s.ftd, { width: pct(w.total), backgroundColor: '#fffde7' }]}>
              <Text>{grandTotal.toFixed(0)}</Text>
            </View>
            <View style={[s.ftd, { width: pct(w.agg + w.div + w.pos) }]}><Text> </Text></View>
          </View> */}

          {/* ── Footer: Aggregate row ── */}
          {/* <View style={s.footerRow}>
            <View style={[s.ftd, s.ftdLeft, { width: pct(w.no + w.student + w.reg) }]}>
              <Text>AGGREGATE</Text>
            </View>
            {subjects.map((subj) =>
              cols.map((col) => {
                const agg = filteredRows[0]?.colAggregates?.[col.code]?.aggTotal ?? '-';
                return (
                  <View key={`agg-${subj.id}-${col.code}`} style={[s.ftd, { width: pct(w.cell), color: c.primary }]}>
                    <Text>{agg}</Text>
                  </View>
                );
              })
            )}
            <View style={[s.ftd, { width: pct(w.total + w.agg + w.div + w.pos) }]}><Text> </Text></View>
          </View> */}

          {/* ── Footer: Division row ── */}
          {/* <View style={s.footerRowDiv}>
            <View style={[s.ftd, s.ftdLeft, { width: pct(w.no + w.student + w.reg) }]}>
              <Text>DIVISION</Text>
            </View>
            {subjects.map((subj) =>
              cols.map((col) => {
                const div = filteredRows[0]?.colAggregates?.[col.code]?.division;
                return (
                  <View key={`div-${subj.id}-${col.code}`} style={[s.ftd, { width: pct(w.cell), color: '#1565c0' }]}>
                    <Text>{div ? String(div) : '-'}</Text>
                  </View>
                );
              })
            )}
            <View style={[s.ftd, { width: pct(w.total + w.agg + w.div + w.pos) }]}><Text> </Text></View>
          </View> */}

        </View>

        {/* ── Page footer ── */}
        <View style={s.pageFooter}>
          <Text>
            Raw marks · Aggregate & Division from class grading boundaries · {filteredRows.length} students · Generated {new Date().toLocaleString()} · School Sync
          </Text>
        </View>

      </Page>
    </Document>
  );
};

// ── Wrapper ───────────────────────────────────────────────────────────────────
const ClassMarkListPdf = ({ filteredRows, subjects, assessmentCols, classLabel, termLabel, school, showAggDiv = true, showPosition = true }) => (
  <div style={{ width: '100%' }}>
    <Box mb={1} display="flex" gap={1}>
      <PDFDownloadLink
        document={<ClassMarkListPdfDoc filteredRows={filteredRows} subjects={subjects} assessmentCols={assessmentCols} classLabel={classLabel} termLabel={termLabel} school={school} showAggDiv={showAggDiv} showPosition={showPosition} />}
        fileName={`mark-list-${(classLabel || 'export').replace(/[^a-z0-9]/gi, '_')}-${new Date().toISOString().split('T')[0]}.pdf`}
      >
        {({ loading }) => (
          <Button variant="contained" size="small" disabled={loading}>
            {loading ? 'Generating…' : 'Download PDF'}
          </Button>
        )}
      </PDFDownloadLink>
    </Box>
    <div style={{ width: '100%', height: '600px', border: '1px solid #ddd', borderRadius: 4 }}>
      <PDFViewer style={{ width: '100%', height: '100%' }}>
        <ClassMarkListPdfDoc filteredRows={filteredRows} subjects={subjects} assessmentCols={assessmentCols} classLabel={classLabel} termLabel={termLabel} school={school} showAggDiv={showAggDiv} showPosition={showPosition} />
      </PDFViewer>
    </div>
  </div>
);

export default ClassMarkListPdf;
export { ClassMarkListPdfDoc };
